import { inject, Injectable } from '@angular/core';
import { PdfSearchResult, PdfSearchSummary } from '../models/pdf-viewer.model';
import { PdfViewerService } from './pdf-viewer.service';

/** Options controlling a search run. */
export interface PdfSearchOptions {
  caseSensitive?: boolean;
  /** Hard cap on matches collected, guarding against pathological queries. */
  matchLimit?: number;
}

/**
 * Document text search. Phase 1 locates matches and powers match-to-match navigation by scanning
 * each page's text content (cached on {@link PdfViewerService}). Per-glyph highlight overlays on
 * the rendered canvas are the documented follow-up — this service already returns the match
 * geometry hooks (page + index) those overlays will consume.
 *
 * Instance-scoped: provided alongside `PdfViewerService` in the component so it shares the same
 * loaded document.
 */
@Injectable()
export class PdfSearchService {
  private readonly viewer = inject(PdfViewerService);
  private readonly snippetRadius = 40;

  async search(query: string, options: PdfSearchOptions = {}): Promise<PdfSearchSummary> {
    const normalized = query.trim();
    if (!normalized) {
      return { query: '', totalMatches: 0, results: [] };
    }

    const caseSensitive = options.caseSensitive ?? false;
    const matchLimit = options.matchLimit ?? 500;
    const needle = caseSensitive ? normalized : normalized.toLowerCase();
    const results: PdfSearchResult[] = [];
    const pageCount = this.viewer.pageCount;

    for (let page = 1; page <= pageCount && results.length < matchLimit; page++) {
      const raw = await this.viewer.getText(page);
      const haystack = caseSensitive ? raw : raw.toLowerCase();

      let from = 0;
      let index = haystack.indexOf(needle, from);
      while (index !== -1 && results.length < matchLimit) {
        results.push({
          pageNumber: page,
          matchIndex: results.length,
          snippet: this.buildSnippet(raw, index, normalized.length),
        });
        from = index + needle.length;
        index = haystack.indexOf(needle, from);
      }
    }

    return { query: normalized, totalMatches: results.length, results };
  }

  private buildSnippet(text: string, at: number, length: number): string {
    const start = Math.max(0, at - this.snippetRadius);
    const end = Math.min(text.length, at + length + this.snippetRadius);
    const prefix = start > 0 ? '…' : '';
    const suffix = end < text.length ? '…' : '';
    return `${prefix}${text.slice(start, end).trim()}${suffix}`;
  }
}
